import { useTranslate } from '../../i18n/index.tsx';

type Props = {
  label: string;
  /** Have saved at least one answer. Their answers already count. */
  responded: number;
  /** Have finished and sent. Absent where the distinction is not tracked. */
  submitted?: number;
  invited: number;
  /** Marks the point the segment must reach to be shown separately. */
  threshold?: number;
};

/**
 * One ratio against its limit.
 *
 * A meter rather than a pie or a two-slice donut: the question is "how far
 * along is this segment", which is a position on a track. The threshold tick
 * is the whole point — it says whether chasing this segment still changes the
 * report.
 *
 * The fill measures how many have *finished*. Anybody who has started and not
 * sent shows as a paler extension of the same bar — they are real progress and
 * their answers already count towards the report, but they are not done, and
 * they are exactly who a reminder is for. Reporting them as finished is what
 * once showed a rater sitting on the last screen as complete.
 *
 * Otherwise the fill measures one thing only: how many of the people asked have
 * answered. It is the same teal whatever happens to the segment afterwards —
 * tinting it to also signal "this will not be broken out" made a full bar mean
 * two things at once, and neither could be read with confidence.
 *
 * That second fact still matters, so it is said in words: a segment too small
 * to ever reach the minimum is labelled "into global". Words carry it; the bar
 * stays a progress bar.
 */
export const Meter = ({ label, responded, submitted, invited, threshold }: Props) => {
  const t = useTranslate();

  const done = submitted ?? responded;
  const started = Math.max(responded - done, 0);
  const share = invited === 0 ? 0 : Math.min(done / invited, 1);
  const startedShare = invited === 0 ? 0 : Math.min(started / invited, 1);
  const thresholdShare = threshold && invited > 0 ? Math.min(threshold / invited, 1) : null;

  /** Everyone answered, and there were never enough of them to break out. */
  const tooSmall =
    threshold !== undefined && invited < threshold && responded >= invited && invited > 0;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-semibold">{label}</span>
        <span className="flex items-baseline gap-1.5">
          {tooSmall && (
            <span className="font-mono text-[9.5px] tracking-[0.06em] text-faint uppercase">
              {t('participants.foldsIntoGlobal')}
            </span>
          )}
          {started > 0 && (
            <span className="font-mono text-[9.5px] tracking-[0.06em] text-faint uppercase">
              {t('dashboard.startedNotSent', { count: started })}
            </span>
          )}
          <span className="font-mono text-[12px] tabular-nums text-soft">
            {done}/{invited}
          </span>
        </span>
      </div>

      <div className="relative mt-1.5 flex h-2 overflow-hidden rounded-full bg-line-soft">
        <span
          className="block h-full bg-teal transition-[width] duration-500"
          style={{ width: `${share * 100}%` }}
        />
        {/* Started, not sent. Same hue, paler: it is the same journey, and the
            number beside it says which part is which. */}
        <span
          className="block h-full bg-teal/35 transition-[width] duration-500"
          style={{ width: `${startedShare * 100}%` }}
        />
        {thresholdShare !== null && thresholdShare < 1 && (
          // A hairline, not a second colour: it marks a position, not a value.
          <span
            aria-hidden
            className="absolute top-[-2px] h-3 w-px bg-soft"
            style={{ left: `${thresholdShare * 100}%` }}
          />
        )}
      </div>
    </div>
  );
};
